// handleOrder.js
'use strict';
import { ADD_ORDERS, ADD_LIST_ITEMS } from '../ActionTypes.js';

import {
  orderStatusConstant,
  SHOP_ID,
  OrderStatus,
} from '../resource/shopConstants.js';
import { gtm as gtmDebug } from '../resource/debug.js';
import getDecimalFactorByCurrency from '../resource/getDecimalFactorByCurrency.js';

import mergeMeData from '../action/mergeMeData.js';
import pushToastr from '../action/pushToastr.js';
import resetUserMessageViewScope from '../action/resetUserMessageViewScope.js';
import toggleModal from '../action/toggleModal.js';
import toggleSubscribeAndUnlockModal from '../action/toggleSubscribeAndUnlockModal.js';
import fetchUserSubscription from '../action/fetchUserSubscription.js';

import getOrderData from '../selector/getOrderData.js';
import getProductData from '../selector/getProductData.js';

const log = gtmDebug.extend('log:GtmLoader');

/**
 * Handle order
 * @kind action
 * @param {string} {status} - order status.
 * @param {string} {orderId} - order id.
 * @param {string} {shopId} - shop id.
 * @param {string} {currency} - order currency.
 * @param {string} {email} - email.
 * @param {number} {amount} - order amount.
 * @param {array} {items} - product items.
 * @return {Promise} Action promise.
 */
const handleOrder =
  (payload = {}) =>
  async dispatch => {
    const { status, orderId } = payload;
    dispatch({
      type: ADD_ORDERS,
      payload: {
        orders: {
          [orderId]: payload,
        },
      },
    });
    switch (status) {
      case orderStatusConstant.FAILED: {
        return dispatch(handleOrderFailed(payload));
      }
      case orderStatusConstant.SUCCEEDED: {
        return dispatch(handleOrderSucceed(payload));
      }
      case orderStatusConstant.PENDING: {
        return dispatch(handleOrderPending(payload));
      }
      default: {
        return dispatch({ type: '' });
      }
    }
  };

export default handleOrder;

/**
 * Handle order pending event
 * @param {object} {payload} - event payload
 * @return {Promise} Action promise.
 */
const handleOrderPending = payload => async (dispatch, getState) => {
  const { orderId } = payload;
  const shopId = getOrderData(getState(), orderId, 'shopId');
  const isSubscriptionShop = SHOP_ID.subscription === shopId;
  const isInAppPurchase = getOrderData(getState(), orderId, 'isInAppPurchase');
  if (!isSubscriptionShop) {
    return dispatch(
      toggleModal({
        modalId: isInAppPurchase
          ? `${shopId}.OrderProcessingInAppAlert`
          : `${shopId}.OrderProcessingAlert`,
        payload: {
          orderId,
        },
        isOpen: true,
        modalOption: {
          isHigherThanAll: true,
        },
      })
    );
  }
  return dispatch({ type: '' });
};

/**
 * Handle order succeed event
 * @param {object} {payload} - event payload
 * @return {Promise} Action promise.
 */
const handleOrderSucceed = payload => async (dispatch, getState) => {
  const { orderId, email, items, amount, currency = '' } = payload;
  const shopId = getOrderData(getState(), orderId, 'shopId');
  const isSubscriptionShop = SHOP_ID.subscription === shopId;
  const isInAppPurchase = getOrderData(getState(), orderId, 'isInAppPurchase');
  try {
    log('gtm purchase event sending');
    if (typeof window !== 'undefined' && window['gtmDataLayer']) {
      window['gtmDataLayer'].push({ ecommerce: null }); // Clear the previous ecommerce object.
      const decimalFactor = getDecimalFactorByCurrency({ currency });
      const gtmEventPayload = {
        event: 'purchase',
        ecommerce: {
          transaction_id: orderId,
          currency: currency.toUpperCase(),
          value: amount / decimalFactor,
          items: (items || []).map(item => ({
            item_id: item.id,
            item_name: item.productId,
            price: item.amount / decimalFactor,
            quantity: item.quantity,
          })),
        },
      };
      window['gtmDataLayer'].push(gtmEventPayload);
      log('gtm purchase event payload', {
        gtmEventPayload,
      });
    }
    log('gtm purchase sent');
  } catch (error) {
    log('gtm purchase event send failed', {
      error,
    });
  }
  if (email) {
    dispatch(mergeMeData({ field: 'orderEmail', value: email }));
  }
  const subscribeUserId = getOrderData(getState(), orderId, 'subscribeUserId');
  if (subscribeUserId) {
    dispatch(resetUserMessageViewScope({ userId: subscribeUserId }));
  } else {
    items.forEach(item => {
      // Save bought product ids to show correct bought status on product detail page.
      const productType = getProductData(getState(), [item.productId], 'type');
      if (productType) {
        dispatch({
          type: ADD_LIST_ITEMS,
          payload: {
            selectPath: [
              'order',
              OrderStatus.BOUGHT,
              productType,
              'productIds',
            ],
            itemIds: [item.productId],
          },
        });
      }
      dispatch(resetUserMessageViewScope({ productId: item.productId }));
    });
  }
  if (!isSubscriptionShop) {
    return dispatch(
      toggleModal({
        modalId: isInAppPurchase
          ? `${shopId}.OrderCompleteInAppAlert`
          : `${shopId}.OrderCompleteAlert`,
        payload: {
          orderId,
        },
        isOpen: true,
        modalOption: {
          isHigherThanAll: true,
        },
      })
    );
  }
  dispatch(
    pushToastr({
      textKey: 'subscribe_succeed',
      color: 'success',
    })
  );
  dispatch(
    toggleSubscribeAndUnlockModal({
      isOpen: false,
    })
  );
  return dispatch(fetchUserSubscription({ userId: subscribeUserId }));
};

/**
 * Handle order failed event
 * @param {object} {payload} - event payload
 * @return {Promise} Action promise.
 */
const handleOrderFailed = payload => async (dispatch, getState) => {
  const { orderId } = payload;
  const shopId = getOrderData(getState(), orderId, 'shopId');
  const isSubscriptionShop = SHOP_ID.subscription === shopId;
  const isInAppPurchase = getOrderData(getState(), orderId, 'isInAppPurchase');
  if (!isSubscriptionShop) {
    return dispatch(
      toggleModal({
        modalId: isInAppPurchase
          ? `${shopId}.OrderCompleteInAppAlert`
          : `${shopId}.OrderCompleteAlert`,
        payload: {
          orderId,
        },
        isOpen: true,
        modalOption: {
          isHigherThanAll: true,
        },
      })
    );
  }
  return dispatch(
    pushToastr({
      textKey: 'subscribe_failed',
      color: 'error',
    })
  );
};
